import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Group as GroupIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  People as PeopleIcon,
  Folder as FolderIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Group, CreateGroupDTO } from '../models/Group';
import { useMyGroups, usePublicGroups, useCreateGroup, useJoinGroup } from '../hooks/useQueries';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroup, setNewGroup] = useState<CreateGroupDTO>({
    name: '',
    description: '',
    isPublic: false,
  });

  const { data: myGroupsData = [], isLoading: myLoading } = useMyGroups();
  const { data: publicGroupsData = [], isLoading: pubLoading } = usePublicGroups();
  const createMutation = useCreateGroup();
  const joinMutation = useJoinGroup();

  const loading = myLoading || pubLoading;
  const myGroups = myGroupsData;
  const publicGroups = publicGroupsData.filter(
    pg => !myGroupsData.some(mg => mg.id === pg.id)
  );

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) return;
    
    try {
      const created = await createMutation.mutateAsync(newGroup);
      setCreateDialogOpen(false);
      setNewGroup({ name: '', description: '', isPublic: false });
      navigate(`/groups/${created.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create group');
    }
  };

  const handleJoinGroup = async (groupId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await joinMutation.mutateAsync(groupId);
    } catch (err: any) {
      setError(err.message || 'Failed to join group');
    }
  };

  const GroupCard: React.FC<{ group: Group; isMember: boolean }> = ({ group, isMember }) => (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': { boxShadow: 6 },
      }}
      onClick={() => navigate(`/groups/${group.id}`)}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            {group.avatarUrl ? (
              <img src={group.avatarUrl} alt={group.name} style={{ width: '100%' }} />
            ) : (
              <GroupIcon />
            )}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" noWrap>
              {group.name}
            </Typography>
            {group.currentUserRole && (
              <Chip 
                size="small" 
                label={group.currentUserRole} 
                color={group.currentUserRole === 'ADMIN' ? 'primary' : 'default'}
              />
            )}
          </Box>
          <Tooltip title={group.isPublic ? 'Public Group' : 'Private Group'}>
            {group.isPublic ? <PublicIcon color="action" /> : <LockIcon color="action" />}
          </Tooltip>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
          {group.description || 'No description'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PeopleIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {group.memberCount} members
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FolderIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {group.collectionCount} collections
            </Typography>
          </Box>
        </Box>

        {group.organizations && group.organizations.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {group.organizations.slice(0, 3).map(org => (
              <Chip
                key={org.id}
                icon={<BusinessIcon />}
                label={org.name}
                size="small"
                variant="outlined"
              />
            ))}
            {group.organizations.length > 3 && (
              <Chip label={`+${group.organizations.length - 3}`} size="small" />
            )}
          </Box>
        )}

        {!isMember && group.isPublic && (
          <Button
            variant="contained"
            size="small"
            sx={{ mt: 2, minHeight: 40 }}
            onClick={(e) => handleJoinGroup(group.id, e)}
            fullWidth
          >
            Join Group
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Groups
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          fullWidth={false}
          sx={{ 
            minHeight: 44,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Create Group
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs 
        value={tabValue} 
        onChange={(_, v) => setTabValue(v)} 
        sx={{ 
          mb: 2,
          '& .MuiTab-root': {
            minHeight: { xs: 48, sm: 48 },
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
          }
        }}
        variant="fullWidth"
      >
        <Tab label={`My Groups (${myGroups.length})`} />
        <Tab label={`Discover (${publicGroups.length})`} />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {myGroups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              You haven't joined any groups yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a new group or explore public groups
            </Typography>
            <Button variant="outlined" onClick={() => setTabValue(1)}>
              Explore Public Groups
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
            {myGroups.map(group => (
              <Box key={group.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.33% - 16px)' } }}>
                <GroupCard group={group} isMember={true} />
              </Box>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {publicGroups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PublicIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No public groups available
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
            {publicGroups.map(group => (
              <Box key={group.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.33% - 16px)' } }}>
                <GroupCard group={group} isMember={false} />
              </Box>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Create Group Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          }
        }}
      >
        <DialogTitle>Create New Group</DialogTitle>
        <DialogContent sx={{ pb: { xs: 2, sm: 3 } }}>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            value={newGroup.name}
            onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
            required
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newGroup.description}
            onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={newGroup.isPublic ? <PublicIcon /> : <LockIcon />}
              label={newGroup.isPublic ? 'Public' : 'Private'}
              onClick={() => setNewGroup({ ...newGroup, isPublic: !newGroup.isPublic })}
              color={newGroup.isPublic ? 'primary' : 'default'}
              clickable
            />
            <Typography variant="body2" color="text.secondary">
              {newGroup.isPublic 
                ? 'Anyone can find and view this group' 
                : 'Only members can view this group'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateGroup} 
            variant="contained" 
            disabled={!newGroup.name.trim() || createMutation.isPending}
            sx={{ minHeight: 44 }}
          >
            {createMutation.isPending ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Groups;
